import classnames from "classnames";
import Icons from "@components/icons.ts";
import { Filters } from "../Filters";
import { ComboboxGroupedButtons } from "@components/ComboBoxes/ComboboxButtonGroup";
import { ComboboxButtonWrapper } from "@components/ComboBoxes/ComboboxButtonWrapper";
import { ComboboxButtonMultiSelect } from "@components/ComboBoxes/ComboboxButtonMultiSelect";
import { ComboboxGroupButton } from "@components/ComboBoxes/ComboboxButton";
import type {
  Payors,
  Specialties,
  States,
} from "@services/filter-api/types";
import { useEffect, useState } from "preact/hooks";
import type { GravityForm } from "@services/wordpress/wordpress.types";
import "./index.scss";
import { Email } from "@components/Forms/GravityFormComponent/Inputs/Email";
import styles from './index.module.scss';
import { useFilterSync } from "@hooks/useFilterSync";

// Helper function to get URL parameter value
const getUrlParameter = (paramName: string): string | null => {
  if (typeof window !== 'undefined') {
    return new URLSearchParams(window.location.search).get(paramName);
  }
  return null;
};

export const FilterComponentGroup = ({
  email_capture_form,
  enable_email_capture_modal = false,
  specialties: specialty,
  states: statesFilters,
  insurance,
  optional_filter_props,
  filter_buttons_props,
  new_styles,
  tabbed_filters,
  type_of_care,
  type_of_care_filter_tabs,
  isMobile: propIsMobile,
  centeredFilters,
  preSelectedLocation,
  preSelectedInsurance,
  preSelectedSpecialties,
}: {
  centeredFilters?: boolean;
  email_capture_form?: GravityForm;
  enable_email_capture_modal?: boolean;
  states: States;
  specialties: Specialties,
  optional_filter_props?: Array<"state" | "needs" | "insurance">;
  insurance: Payors
  filter_buttons_props?: Array<{
    name: string;
    label: string;
    is_optional: boolean;
    is_multi_select: boolean;
    placeholder: string;
  }>;
  isMobile?: boolean;
  new_styles?: boolean;
  tabbed_filters?: boolean;
  type_of_care?: string;
  type_of_care_filter_tabs?: Array<{
    icon: string;
    label: string;
    parameter_value: string;
    short_label?: string;
  }>;
  preSelectedLocation?: string;
  preSelectedInsurance?: string;
  preSelectedSpecialties?: string;
}) => {
  const [iconComponents, setIconComponents] = useState<Record<string, any>>({});
  const [openEmailCaptureModal, setOpenEmailCaptureModal] = useState(false);
  const [showValidationMessage, setShowValidationMessage] = useState(false);
  const [redirectUrl, setRedirectUrl] = useState<string>("");

  // Use the filter sync hook
  const {
    selectedItems: syncedSelectedItems,
    typeOfCare: syncedTypeOfCare,
    errors: syncedErrors,
    handleGetSelectedState: syncedHandleGetSelectedState,
    handleGetSelectedInsurance: syncedHandleGetSelectedInsurance,
    handleGetSelectedNeeds: syncedHandleGetSelectedNeeds,
    handleTypeOfCareChange: syncedHandleTypeOfCareChange,
    handleValidationUpdate: syncedHandleValidationUpdate,
    syncedFields,
  } = useFilterSync();

  // Initialize type of care with precedence: URL parameter > prop > synced state
  const [typeOfCare, setTypeOfCare] = useState<string | undefined>(() => {
    // Check if there's a URL parameter first
    const urlTypeOfCare = getUrlParameter('typeOfCare');
    if (urlTypeOfCare) {
      return urlTypeOfCare;
    }
    // Fall back to prop or synced state
    return type_of_care || syncedTypeOfCare;
  });

  // Update local type of care when synced changes (but only if URL parameter doesn't exist)
  useEffect(() => {
    const urlTypeOfCare = getUrlParameter('typeOfCare');
    
    // Only update from synced state if there's no URL parameter
    if (!urlTypeOfCare && syncedTypeOfCare !== undefined) {
      setTypeOfCare(syncedTypeOfCare);
    }
  }, [syncedTypeOfCare]);

  // Sync URL parameter to global state on initial load
  useEffect(() => {
    const urlTypeOfCare = getUrlParameter('typeOfCare');
    if (urlTypeOfCare && urlTypeOfCare !== syncedTypeOfCare) {
      syncedHandleTypeOfCareChange(urlTypeOfCare);
    }
  }, [syncedTypeOfCare, syncedHandleTypeOfCareChange]);

  // Update tab highlighting when typeOfCare changes
  useEffect(() => {
    if (!tabbed_filters || !typeOfCare) return;

    const containers = document.querySelectorAll('[data-combobox-tab-group]');
    containers.forEach(container => {
      // Remove active class from all tabs in this container
      const allTabs = container.querySelectorAll('[data-combobox-tab-group-tab]');
      allTabs.forEach(tab => tab.classList.remove('combobox-tab-group__tab--active'));
      
      // Add active class to the matching tab
      const activeTab = container.querySelector(`[data-combobox-tab-group-tab="${typeOfCare}"]`);
      if (activeTab) {
        activeTab.classList.add('combobox-tab-group__tab--active');
      }
    });
  }, [typeOfCare, tabbed_filters]);

  // Handles tab click events
  useEffect(() => {
    if (!tabbed_filters) return;

    const containers = document.querySelectorAll('[data-combobox-tab-group]');
    if (containers.length === 0) return;

    const handleTabClick = (e: Event) => {
      const target = e.target as Element;
      const clickedTab = target?.closest('[data-combobox-tab-group-tab]') as HTMLElement;
      if (!clickedTab) return;

      // Get the previously active tab in the same container
      const activeTab = clickedTab.parentElement?.querySelector('.combobox-tab-group__tab--active');
      
      // Only update if clicking a different tab
      if (activeTab !== clickedTab) {
        activeTab?.classList.remove('combobox-tab-group__tab--active');
        clickedTab.classList.add('combobox-tab-group__tab--active');
        const newTypeOfCare = clickedTab.dataset.comboboxTabGroupTab;
        setTypeOfCare(newTypeOfCare);
        syncedHandleTypeOfCareChange(newTypeOfCare);
      }
    };

    containers.forEach(container => {
      container.addEventListener('click', handleTabClick);
    })

    return () => {
      containers.forEach(container => {
        container.removeEventListener('click', handleTabClick);
      })
    }
  }, [syncedHandleTypeOfCareChange]);

  // Handles fetching of icons that are used in the tabs
  useEffect(() => {
    const loadIcons = async () => {
      const icons: Record<string, any> = {};
      if (type_of_care_filter_tabs) {
        for (const tab of type_of_care_filter_tabs) {
          if (!icons[tab.icon]) {
            try {
              const module = await import(`../../../icons/${tab.icon}-icon.tsx`);
              icons[tab.icon] = module.default;
            } catch (error) {
              icons[tab.icon] = null;
            }
          }
        }
      }
      setIconComponents(icons);
    };

    loadIcons();
  }, [type_of_care_filter_tabs]);

  // Helper function to render icon
  const renderIcon = (iconName: string) => {
    const IconComponent = iconComponents[iconName];
    return IconComponent ? <IconComponent /> : null;
  };

  return (
    <Filters
      email_capture_form={email_capture_form}
      specialties={specialty}
      states={statesFilters}
      insurance={insurance}
      optional_filter_props={optional_filter_props}
      filter_buttons_props={filter_buttons_props}
      type_of_care={typeOfCare}
      onRedirectUrlChange={setRedirectUrl}
      preSelectedLocation={preSelectedLocation}
      preSelectedInsurance={preSelectedInsurance}
      preSelectedSpecialties={preSelectedSpecialties}
    >
      {({
        selectedItems,
        handleRedirectClick,
        getRedirectUrl,
        requiredFiltersAllValid,
        selectLater,
        handleSelectLater,
        isMobile: filterIsMobile,
        insurance,
        specialty,
        statesFilters,
        handleOpenStates,
        handleOpenInsurance,
        handleOpenNeeds,
        handleGetSelectedInsurance,
        handleGetSelectedState,
        openInsurance,
        openNeeds,
        openStates,
        handleGetSelectedNeeds,
        errors,
      }) => {
        const isMobile = propIsMobile ?? filterIsMobile;

        // Combine local and synced state
        // Use synced state if it has been explicitly synced (including null for deselection)
        // Otherwise fall back to local state
        const combinedSelectedItems = {
          state: syncedFields.state ? syncedSelectedItems.state : selectedItems.state,
          insurance: syncedFields.insurance ? syncedSelectedItems.insurance : selectedItems.insurance,
          specialty: syncedFields.specialty ? syncedSelectedItems.specialty : selectedItems.specialty,
        };

        // Combine validation errors
        // Prioritize local errors when they're empty (cleared) over synced errors
        const combinedErrors = {
          state: errors?.state === "" ? "" : (syncedFields.errors ? syncedErrors.state : errors?.state || ""),
          insurance: errors?.insurance === "" ? "" : (syncedFields.errors ? syncedErrors.insurance : errors?.insurance || ""),
        };

        // Create combined handlers that update both local and synced state
        const combinedHandleGetSelectedState = (item: any) => {
          handleGetSelectedState(item);
          syncedHandleGetSelectedState(item);
        };

        const combinedHandleGetSelectedInsurance = (item: any) => {
          handleGetSelectedInsurance(item);
          syncedHandleGetSelectedInsurance(item);
        };

        const combinedHandleGetSelectedNeeds = (items: any[]) => {
          handleGetSelectedNeeds(items);
          syncedHandleGetSelectedNeeds(items);
        };

        // Sync validation errors when they change in the Filters component
        useEffect(() => {
          if (errors && (errors.state !== "" || errors.insurance !== "")) {
            syncedHandleValidationUpdate(errors);
          }
        }, [errors, syncedHandleValidationUpdate]);

        // Add event listener for opening insurance dropdown
        useEffect(() => {
          const handleOpenInsuranceDropdown = (event: CustomEvent) => {
            // Find the filter modal dialog
            const filterModalDialog = document.querySelector('[data-filter-modal-dialog]');
            if (!filterModalDialog) return;

            // Find the insurance button inside the filter modal
            const insuranceButton = filterModalDialog.querySelector('[data-combobox-button="Insurance"]');
            if (insuranceButton instanceof HTMLElement) {
              const clickEvent = new MouseEvent('click', {
                bubbles: false,
                cancelable: true
              });
              insuranceButton.dispatchEvent(clickEvent);
            }
          };

          document.addEventListener('open-filter-modal-insurance-filters', handleOpenInsuranceDropdown as EventListener);
          
          return () => {
            document.removeEventListener('open-filter-modal-insurance-filters', handleOpenInsuranceDropdown as EventListener);
          };
        }, []);

        const handleEmailCaptureSubmit = async (event: Event) => {
          event.preventDefault();
          const formEl = event.target as HTMLFormElement;

          // Dynamically populate hidden fields
          const locationInput = formEl.querySelector('input[value="{pre_srp_location}"]') as HTMLInputElement;
          if (locationInput && combinedSelectedItems.state?.value) {
            locationInput.value = combinedSelectedItems.state.value;
          }

          const insuranceInput = formEl.querySelector('input[value="{pre_srp_insurance}"]') as HTMLInputElement;
          if (insuranceInput && combinedSelectedItems.insurance?.value) {
            insuranceInput.value = combinedSelectedItems.insurance.value;
          }

          const needsInput = formEl.querySelector('input[value="{pre_srp_needs}"]') as HTMLInputElement;
          if (needsInput && combinedSelectedItems.specialty?.values) {
            needsInput.value = Array.from(combinedSelectedItems.specialty.values()).map(s => s.value).join(', ');
          }

          const url = getRedirectUrl?.() || "";
          setRedirectUrl(url);
          
          const redirectUrlInput = formEl.querySelector('input[value="{pre_srp_url}"]') as HTMLInputElement;
          if (redirectUrlInput && url) redirectUrlInput.value = url;

          const formData = new FormData(formEl);

          try {
            const response = await fetch(`${import.meta.env.PUBLIC_WP_APP_URL}/wp-json/gf/v2/forms/${email_capture_form?.id}/submissions/`, {
              method: 'POST',
              body: formData,
            });

            const json = await response.json();

            if (json?.is_valid) {
              setShowValidationMessage(false);
              handleRedirectClick?.(event);
            } else {
              setShowValidationMessage(true);
            }
          } catch (err) {
            console.error("Form submission failed", err);
          }
        };

        useEffect(() => {
          if (!openEmailCaptureModal) return;

          const handleClickOutside = (event: MouseEvent) => {
            const modalContent = document.querySelector(`.${styles.emailCaptureModalContent}`);
            if (modalContent && !modalContent.contains(event.target as Node)) {
              setOpenEmailCaptureModal(false);
              document.body.style.overflow = '';
            }
          };

          const formEl = document.querySelector(`.${styles.emailCaptureModalContent}`) as HTMLFormElement | null;
          if (formEl) {
            formEl.addEventListener("submit", handleEmailCaptureSubmit);
          }

          document.addEventListener("mousedown", handleClickOutside);

          return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            if (formEl) {
              formEl.removeEventListener("submit", handleEmailCaptureSubmit);
            }
          };
        }, [openEmailCaptureModal]);

        return (
          <div
            className={classnames(
            "filter-component-group relative w-full md:w-fit", {
              "filter-component-group--tabbed-filters": tabbed_filters,
            })}
          >
            {tabbed_filters && type_of_care_filter_tabs && (
              <div className="combobox-tab-group" data-combobox-tab-group>
                {type_of_care_filter_tabs.map(tab => (
                  <button
                    key={tab.parameter_value}
                    className={`combobox-tab-group__tab ${typeOfCare === tab.parameter_value ? 'combobox-tab-group__tab--active' : ''}`}
                    type="button"
                    data-combobox-tab-group-tab={tab.parameter_value}
                  >
                    {!centeredFilters ? renderIcon(tab.icon) : null}
                    <span className="combobox-tab-group__label--short">
                      {tab.short_label !== '' ? tab.short_label : tab.label}
                    </span>
                    <span className="combobox-tab-group__label">
                      {tab.label}
                    </span>
                  </button>
                ))}
              </div>
            )}
            <ComboboxGroupedButtons
              new_styles={new_styles}
              tabbed_filters={tabbed_filters}
            >
              <ComboboxButtonWrapper
                name="State"
                new_styles={new_styles}
                optional={filter_buttons_props?.[0]?.is_optional}
                handleOpenClick={handleOpenStates}
                buttonWrapperClass={classnames(
                  "w-11/12 md:rounded-s-lg border-b md:border my-2 md:my-0",
                  {
                    "border-[#B35436] border-error": combinedErrors?.state,
                    "border-gt-black": !combinedErrors?.state,
                  },
                )}
                open={openStates}
                error={combinedErrors?.state}
                selectedItem={combinedSelectedItems.state}
                isMobile={isMobile}
                endIcon={<Icons.CaretDownIcon open={openStates} />}
                data={
                  combinedSelectedItems.insurance
                    ? statesFilters.map((_state) => {
                        const states = insurance.find(
                          (_insurance) =>
                            _insurance.value === combinedSelectedItems.insurance?.value,
                        )?.states;

                        const hasStates = states?.find(
                          (_s) =>
                            _s.abbreviation?.toLowerCase() ===
                            _state.abbreviation?.toLowerCase(),
                        );
                        if (hasStates) {
                          return {
                            ..._state,
                            filtered: true,
                          };
                        }
                        return _state;
                      })
                    : statesFilters
                }
                placeholder={
                  filter_buttons_props?.[0]
                    ? `${filter_buttons_props[0].placeholder}`
                    : `Select your state`
                }
                label={
                  filter_buttons_props?.[0]
                    ? `${filter_buttons_props[0].label}${filter_buttons_props[0].is_optional ? "" : "*"}`
                    : `location`
                }
                handleGetSelectedItem={combinedHandleGetSelectedState}
              />

              <ComboboxButtonWrapper
                name="Insurance"
                new_styles={new_styles}
                optional={filter_buttons_props?.[1]?.is_optional}
                handleOpenClick={handleOpenInsurance}
                open={openInsurance}
                error={combinedErrors?.insurance}
                buttonWrapperClass={classnames(
                  "w-11/12 md:border-t border-b md:border-r my-2 md:my-0",
                  {
                    "border-[#B35436] border-error": combinedErrors?.insurance,
                    "border-gt-black": !combinedErrors?.insurance,
                  },
                )}
                selectedItem={combinedSelectedItems.insurance}
                isMobile={isMobile}
                endIcon={<Icons.CaretDownIcon open={openInsurance} />}
                data={
                  // find insurance with the selected state
                  combinedSelectedItems.state
                    ? insurance.map((_insurance) => {
                        const insurerHasSelectedState = _insurance.states?.find(
                          (_s) =>
                            _s.abbreviation?.toLowerCase() ===
                            combinedSelectedItems.state?.abbreviation?.toLowerCase(),
                        );

                        if (insurerHasSelectedState) {
                          return {
                            ..._insurance,
                            filtered: true,
                          };
                        }
                        return _insurance;
                      })
                    : insurance
                }
                placeholder={
                  filter_buttons_props?.[1]
                    ? `${filter_buttons_props[1].placeholder}`
                    : `Select your carrier`
                }
                label={
                  filter_buttons_props?.[1]
                    ? `${filter_buttons_props[1].label}${filter_buttons_props[1].is_optional ? "" : "*"}`
                    : `insurance`
                }
                handleGetSelectedItem={combinedHandleGetSelectedInsurance}
              />
              <ComboboxButtonMultiSelect
                name="Specialties"
                isMobile={isMobile}
                handleOpenClick={handleOpenNeeds}
                buttonWrapperClass='border-gt-black w-11/12 md:border-t border-b md:border-r my-2 md:my-0'
                open={openNeeds}
                specialties={specialty}
                endIcon={<Icons.CaretDownIcon open={openNeeds} />}
                placeholder={
                  filter_buttons_props?.[2]
                    ? `${filter_buttons_props[2].placeholder}`
                    : `Select your needs`
                }
                label={
                  filter_buttons_props?.[2]
                    ? `${filter_buttons_props[2].label}${filter_buttons_props[2].is_optional ? "" : "*"}`
                    : `needs`
                }
                optional={filter_buttons_props?.[2]?.is_optional}
                handleGetSelectedItems={combinedHandleGetSelectedNeeds}
                selectedItems={combinedSelectedItems?.specialty}
                selectLater={selectLater}
                handleSelectLater={handleSelectLater}
                centeredFilters={centeredFilters}
              />

              <ComboboxGroupButton
                variant='contained'
                button='submit'
                buttonWrapperClass='border-gt-black w-11/12 rounded-e-lg md:border-t md:border-b md:border-r md:border-l-0 my-4 md:my-0'
                label={
                  <div className='flex items-center w-full justify-center'>
                    <Icons.SearchIcon />
                    <span className='ComboboxButton__search-text ml-1'>Search</span>
                  </div>
                }
                onClick={(e) => {
                  if (enable_email_capture_modal && email_capture_form && requiredFiltersAllValid?.()) {
                    setOpenEmailCaptureModal(true);
                    document.body.style.overflow = 'hidden';
                  } else {
                    handleRedirectClick?.(e);
                  }
                }}
                open={openEmailCaptureModal}
              >
                {({ open }) => (
                  openEmailCaptureModal && email_capture_form && (
                    <div className={styles.emailCaptureModal}>
                      <div className={styles.emailCaptureModalContent}>
                        <form>
                          {email_capture_form.fields.map(field => {
                            if (field.type === 'html' && field.content) {
                              return (
                                <div dangerouslySetInnerHTML={{ __html: field.content }} />
                              );
                            } else if (field.type === 'email') {
                              return (
                                <Email
                                  autocompleteAttribute={field.autocompleteAttribute}
                                  enableAutocomplete={field.enableAutocomplete}
                                  id={`${field.id}`}
                                  label={field.label}
                                  placeholder={field.placeholder}
                                  showValidationMessage={showValidationMessage}
                                />
                              );
                            } else if (field.type === 'hidden') {
                              return (
                                <input id={String(field.id)} type="hidden" value={field.defaultValue} name={`input_${field.id}`} />
                              );
                            }
                          })}

                          {email_capture_form.button && (
                            <button
                              className={styles.emailCaptureModalButton}
                              id={email_capture_form.button.id}
                              type="submit"
                            >
                              {email_capture_form.button.text}
                            </button>
                          )}
                        </form>

                        <button className={styles.emailCaptureModalLink} type="button" onClick={handleRedirectClick}>
                          Continue to my results
                        </button>
                      </div>
                    </div>
                  )
                )}
              </ComboboxGroupButton>
            </ComboboxGroupedButtons>
          </div>
        );
      }}
    </Filters>
  );
};
